import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faBriefcase, faFileCode, faFilePdf, faMale, faFemale, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import useToken from 'hooks/useToken';
import ModalDownloadingDocument from 'akasia-components/Contabilidad/centro-xml/common/ModalDownloadingDocument';

const NominaCard = ({ nomina, isSelected, onSelect, rfcEmisor }) => {
    const { token } = useToken();
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadType, setDownloadType] = useState('generic');

    const {
        vSerie,
        vFolio,
        NumEmpleado,
        vRfcReceptor,
        vRazonSocialReceptor,
        TipoNomina,
        FechaPago,
        TotalPercepciones,
        TotalDeducciones,
        dTotal,
        Departamento,
        Puesto,
        vUUIDFactura,
        RegistroPatronal,
        CURP,
        NSS,
        SBC,
        Antiguedad,
        FechaInicioRelLaboral,
        CuentaBancaria,
        Banco,
        TotalSueldos,
        FechaInicialPago,
        FechaFinalPago
    } = nomina;

    // ── Helpers ────────────────────────────────────────────────────────────────
    const formatCurrency = (value) => {
        if (value == null) return '—';
        return parseFloat(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('es-MX', {
            day: '2-digit', month: 'short', year: 'numeric'
        }).replace(/(?:^|\s)([a-z])/g, c => c.toUpperCase());
    };

    // ── Descargar XML (endpoint backend con auth) ──────────────────────────────
    const descargarXML = async (e) => {
        if (e) e.stopPropagation();
        if (!vUUIDFactura) return;
        setIsDownloading(true);
        setDownloadType('xml');
        try {
            const aniomes = FechaPago ? (FechaPago.substring(0, 4) + FechaPago.substring(5, 7)) : '';
            const rfc = rfcEmisor || '';
            const response = await fetch(`/api/contabilidad/Nomina/DescargarXMLNomina?uuid=${vUUIDFactura}&aniomes=${aniomes}&rfc=${rfc}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${vUUIDFactura || `${vSerie}-${vFolio}`}.xml`;
                a.click();
                URL.revokeObjectURL(url);
            } else {
                console.error("Error al descargar XML", await response.text());
            }
        } catch (error) {
            console.error("Error de red al descargar XML", error);
        } finally {
            setIsDownloading(false);
        }
    };

    // ── Descargar PDF (endpoint backend con auth) ──────────────────────────────
    const descargarPDF = async (e) => {
        if (e) e.stopPropagation();
        if (!vUUIDFactura) return;
        setIsDownloading(true);
        setDownloadType('pdf');
        try {
            const response = await fetch(`/api/contabilidad/Nomina/DescargarPDFNomina?uuid=${vUUIDFactura}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${vUUIDFactura}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
            } else {
                console.error("Error al descargar PDF", await response.text());
            }
        } catch (error) {
            console.error("Error de red al descargar PDF", error);
        } finally {
            setIsDownloading(false);
        }
    };

    const tipoNominaBadgeVariant = TipoNomina === 'O' ? 'primary' : TipoNomina === 'E' ? 'warning' : 'secondary';
    const tipoNominaLabel = TipoNomina === 'O' ? 'Ordinaria' : TipoNomina === 'E' ? 'Extraordinaria' : TipoNomina;
    // ── Sexo desde CURP ────────────────────────────────────────────────────────
    const getGenderIcon = () => {
        if (!CURP || CURP.length < 11) return null;
        const genderChar = CURP.charAt(10).toUpperCase();
        if (genderChar === 'H') {
            return <FontAwesomeIcon icon={faMale} className="text-primary me-2" title="Hombre" />;
        } else if (genderChar === 'M') {
            return <FontAwesomeIcon icon={faFemale} className="text-danger me-2" title="Mujer" />;
        }
        return null;
    };

    const cardClasses = `mb-3 shadow-sm ${isSelected ? 'border-primary bg-light' : ''}`;
    const cardStyle = {
        transition: 'all 0.2s ease-in-out',
        cursor: onSelect ? 'pointer' : 'default',
        borderWidth: isSelected ? '2px' : '1px'
    };

    const handleCardClick = (e) => {
        // Evitar que el clic en botones o enlaces (como descargar XML/PDF) seleccione la tarjeta
        if (e.target.closest('button') || e.target.closest('a')) {
            return;
        }
        if (onSelect) {
            onSelect();
        }
    };

    return (
        <Card
            className={cardClasses}
            style={cardStyle}
            onClick={handleCardClick}
        >
            <Card.Body>
                <Row className="align-items-center">

                    {/* Columna izquierda: Folio, Serie, Num Empleado, Fecha */}
                    <Col xs={12} md={3} className="mb-2 mb-md-0">
                        <div className="d-flex flex-column">
                            <div className="d-flex align-items-center gap-2 mb-1">
                                {isSelected && (
                                    <FontAwesomeIcon
                                        icon={faCheckCircle}
                                        className="text-primary"
                                        size="lg"
                                    />
                                )}
                                <Link
                                    to={`/contabilidad/nomina/timbrado/visualizacion/${vUUIDFactura}`}
                                    className="fw-bold text-primary text-decoration-none"
                                    style={{ fontSize: '1.1rem' }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {vSerie || 'S/N'}-{vFolio}
                                </Link>
                                <span className={`text-uppercase fw-semibold text-${tipoNominaBadgeVariant}`} style={{ fontSize: '0.75rem' }}>
                                    {tipoNominaLabel}
                                </span>
                            </div>
                            <small className="text-muted">
                                <strong>Periodo:</strong> {FechaInicialPago && FechaFinalPago ? `${formatDate(FechaInicialPago)} - ${formatDate(FechaFinalPago)}` : '—'}
                            </small>
                            <small className="text-muted">
                                <strong>Pago:</strong> {formatDate(FechaPago)}
                            </small>
                            {Departamento && (
                                <small className="text-dark mt-1">
                                    <FontAwesomeIcon icon={faBuilding} className="me-1" />
                                    {Departamento}
                                </small>
                            )}
                            {Puesto && (
                                <small className="text-dark mt-1">
                                    <FontAwesomeIcon icon={faBriefcase} className="me-1" />
                                    {Puesto}
                                </small>
                            )}

                        </div>
                    </Col>

                    {/* Columna central: Empleado (Receptor) + datos del XML */}
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                        <div className="mb-1">
                            <div className="fw-bold text-truncate" title={vRazonSocialReceptor}>
                                {getGenderIcon()}
                                {NumEmpleado ? `${NumEmpleado} - ` : ''}{vRazonSocialReceptor || '—'}
                            </div>
                            <div className="small text-primary fw-medium">
                                RFC: {vRfcReceptor}
                            </div>
                        </div>

                        {/* Registro Patronal resaltado */}
                        {RegistroPatronal && (
                            <div className="small fw-bold text-dark mt-1">
                                <span className="text-muted fw-normal me-1">Reg. Patronal:</span>
                                {RegistroPatronal}
                            </div>
                        )}

                        {/* CURP · NSS · SBC */}
                        {(CURP || NSS || SBC != null) && (
                            <div className="d-flex flex-wrap gap-3 mt-1 small text-muted">
                                {CURP && (
                                    <span title="CURP">
                                        <strong>CURP:</strong> {CURP}
                                    </span>
                                )}
                                {NSS && (
                                    <span title="Número de Seguridad Social">
                                        <strong>NSS:</strong> {NSS}
                                    </span>
                                )}
                                {SBC != null && (
                                    <span title="Salario Base de Cotización">
                                        <strong>SBC:</strong> {formatCurrency(SBC)}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Banco · Antigüedad · Fecha Inicio · Total Sueldos */}
                        {(Banco || CuentaBancaria || Antiguedad || FechaInicioRelLaboral || TotalSueldos != null) && (
                            <div className="d-flex flex-wrap gap-3 mt-1 small text-muted">
                                {Banco && (
                                    <span title="Banco">
                                        <strong>Banco:</strong> {Banco}
                                    </span>
                                )}
                                {CuentaBancaria && (
                                    <span title="Cuenta Bancaria">
                                        <strong>Cuenta:</strong> {CuentaBancaria}
                                    </span>
                                )}
                                {FechaInicioRelLaboral && (
                                    <span title="Fecha de Inicio de Relación Laboral">
                                        <strong>Alta:</strong> {formatDate(FechaInicioRelLaboral)}
                                    </span>
                                )}
                                {Antiguedad && (
                                    <span title="Antigüedad">
                                        <strong>Antigüedad:</strong> {Antiguedad}
                                    </span>
                                )}
                                {TotalSueldos != null && (
                                    <span title="Total Sueldos">
                                        <strong>Sueldos:</strong> {formatCurrency(TotalSueldos)}
                                    </span>
                                )}
                            </div>
                        )}
                    </Col>

                    {/* Columna derecha: Totales */}
                    <Col xs={12} md={3} className="text-md-end">
                        <div className="d-flex flex-column align-items-md-end">
                            <span className="fw-bold text-success mb-1" style={{ fontSize: '1.2rem' }}>
                                {formatCurrency(dTotal)}
                            </span>
                            <small className="text-muted mb-1">
                                <strong>Percepciones:</strong> {formatCurrency(TotalPercepciones)}
                            </small>
                            <small className="text-danger mb-1">
                                <strong>Deducciones:</strong> {formatCurrency(TotalDeducciones)}
                            </small>
                        </div>
                        {/* Botones de descarga alineados a la derecha */}
                        <div className="d-flex gap-2 mt-2 justify-content-end">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Descargar XML</Tooltip>}>
                                <button
                                    className="btn btn-outline-secondary btn-sm py-0 px-2"
                                    onClick={descargarXML}
                                    disabled={!vUUIDFactura}
                                    title="Descargar XML"
                                >
                                    <FontAwesomeIcon icon={faFileCode} className="me-1" />
                                    XML
                                </button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Descargar PDF</Tooltip>}>
                                <button
                                    className="btn btn-outline-danger btn-sm py-0 px-2"
                                    onClick={descargarPDF}
                                    disabled={!vUUIDFactura}
                                    title="Descargar PDF"
                                >
                                    <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                                    PDF
                                </button>
                            </OverlayTrigger>
                        </div>
                    </Col>
                </Row>
            </Card.Body>

            <ModalDownloadingDocument
                show={isDownloading}
                documentType={downloadType}
                title={downloadType === 'pdf' ? 'Generando PDF' : 'Descargando XML'}
            />
        </Card>
    );
};

NominaCard.propTypes = {
    nomina: PropTypes.shape({
        vSerie: PropTypes.string,
        vFolio: PropTypes.string,
        NumEmpleado: PropTypes.string,
        vRfcReceptor: PropTypes.string,
        vRazonSocialReceptor: PropTypes.string,
        TipoNomina: PropTypes.string,
        FechaPago: PropTypes.string,
        TotalPercepciones: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        TotalDeducciones: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        dTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Departamento: PropTypes.string,
        Puesto: PropTypes.string,
        vUUIDFactura: PropTypes.string,
        RegistroPatronal: PropTypes.string,
        CURP: PropTypes.string,
        NSS: PropTypes.string,
        SBC: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Antiguedad: PropTypes.string,
        FechaInicioRelLaboral: PropTypes.string,
        CuentaBancaria: PropTypes.string,
        Banco: PropTypes.string,
        TotalSueldos: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        FechaInicialPago: PropTypes.string,
        FechaFinalPago: PropTypes.string
    }).isRequired,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func,
    rfcEmisor: PropTypes.string
};

export default NominaCard;
